import React, { useEffect } from 'react';

import { Table } from 'antd';

import { OrderSap } from '../../../../../../api-client';
import { ORDER_STATUS } from '../../../../../../utils/constants/order-status';
import { SIZE_CODES } from '../../../../../../utils/constants/size-codes';
import { sortingDateComparer, sortingNumberComparer, sortingStringComparer } from '../../../../../../utils/data-comparer/data-sorting-comparer';
import { formatCurrency } from '../../../../../../utils/data-format-enchanters/currency-enchanter';
import { formatStandardDate } from '../../../../../../utils/data-format-enchanters/datetime-enchanter';
import { aggregateProductSizes, orderTotalByProducts, orderQuantityByProducts, orderLineAmountByProductSize } from '../../../../../../utils/data-format-enchanters/order-products-enchanter';
import { useOrder } from '../../../../../hooks/useOrder';

import './checkout-table-sap.scss';

import BVEmpty from '../../../../BVEmpty/bv-empty';
import BVShimmer from '../../../../BVShimmers/BVShimmer/bv-shimmer';
import BVOrderStatus from '../../../../BVOrderStatus/bv-order-status';

interface ICheckoutTableSap {
    orderId?: number;
}

const CheckoutTableSap: React.FunctionComponent<ICheckoutTableSap> = ({ orderId }) => {
    const {
        handleRetrieveOrderSap,
        retrieveOrderSap,
        retrieveOrderSapLoading
    } = useOrder();

    useEffect(() => {
        if (orderId) {
            handleRetrieveOrderSap(orderId);
        }
    }, [orderId]);

    const _handleProductsByProductSizes = (orderSap: OrderSap) => (
        <Table
            columns={[
                {
                    title: '',
                    align: 'center',
                    dataIndex: 'productImageUrl',
                    render: (value: any, dataItem: any) => (
                        <img src={value} className="checkout-table-sap-img" alt={dataItem.name} />
                    )
                },
                {
                    title: 'Catalog Code',
                    align: 'center',
                    dataIndex: 'productCatCode',
                    sorter: (recordA, recordB) => sortingStringComparer(
                        recordA.productCatCode,
                        recordB.productCatCode
                    )
                },
                {
                    title: 'Sap Code',
                    align: 'center',
                    dataIndex: 'sapCode',
                    sorter: (recordA, recordB) => sortingStringComparer(
                        recordA.sapCode,
                        recordB.sapCode
                    )
                },
                {
                    title: 'Delivery Date',
                    align: 'left',
                    dataIndex: 'productDeliveryDate',
                    render: (value: any) => value ? formatStandardDate(value) : '-',
                    sorter: (recordA, recordB) => sortingDateComparer(
                        recordA.productDeliveryDate,
                        recordB.productDeliveryDate
                    )
                },
                {
                    title: 'Size',
                    align: 'center',
                    dataIndex: 'code',
                    render: (value: any) => value === SIZE_CODES.NO_SIZE ? '-' : value,
                    sorter: (recordA, recordB) => sortingStringComparer(
                        recordA.code,
                        recordB.code
                    )
                },
                {
                    title: 'Unit Price',
                    align: 'right',
                    dataIndex: 'priceNet',
                    render: (value: any) => formatCurrency(value.amount, value.currency),
                    sorter: (recordA, recordB) => sortingNumberComparer(
                        recordA.priceNet?.amount,
                        recordB.priceNet?.amount
                    )
                },
                {
                    title: 'Quantity',
                    align: 'center',
                    dataIndex: 'quantity',
                    sorter: (recordA, recordB) => sortingNumberComparer(
                        recordA.quantity,
                        recordB.quantity
                    ),
                    width: 115
                },
                {
                    title: 'Amount',
                    align: 'right',
                    render: (value: any) => orderLineAmountByProductSize(value).amountFormat,
                    sorter: (recordA, recordB) => sortingNumberComparer(
                        orderLineAmountByProductSize(recordA).amount,
                        orderLineAmountByProductSize(recordB).amount
                    ),
                    width: 200
                }
            ]}
            dataSource={aggregateProductSizes(orderSap.products)}
            pagination={false}
            rowKey={(itemData: any) => `antd-${itemData._arrayKey}`}
        />
    );

    return (
        <div className="checkout-table-sap-container">
            <BVShimmer loading={retrieveOrderSapLoading} type={'shimmer-details'}>
                <BVEmpty dataSource={retrieveOrderSap || []} disclaimer={'No order sap found for given order number.'}>
                    <Table
                        columns={[
                            {
                                title: 'ID',
                                align: 'center',
                                dataIndex: 'id',
                                sorter: (recordA, recordB) => sortingNumberComparer(
                                    recordA.id,
                                    recordB.id
                                )
                            },
                            {
                                title: 'Sap order ID',
                                align: 'center',
                                dataIndex: 'sapOrderCode',
                                sorter: (recordA, recordB) => sortingStringComparer(
                                    recordA.sapOrderCode,
                                    recordB.sapOrderCode
                                )
                            },
                            {
                                title: 'Order Status',
                                align: 'center',
                                dataIndex: 'status',
                                render: (value: any) => (
                                    <div className={`${value.code === ORDER_STATUS.ERROR.code && 'checkout-table-sap-error'}`}>
                                        <BVOrderStatus status={value.code} />
                                    </div>
                                ),
                                sorter: (recordA, recordB) => sortingNumberComparer(
                                    recordA.status?.id,
                                    recordB.status?.id
                                )
                            },
                            {
                                title: 'Ship to',
                                align: 'left',
                                dataIndex: 'facility',
                                width: '17.5%',
                                render: (value: any) => `${value?.sapCode} - ${value?.name} - ${value?.address}, ${value?.city}`
                            },
                            {
                                title: 'Order Error Message',
                                align: 'left',
                                dataIndex: 'sapOrderMessage',
                                width: '30%'
                            },
                            {
                                title: 'Quantity',
                                align: 'center',
                                dataIndex: 'products',
                                render: (value: any) => orderQuantityByProducts(value),
                                sorter: (recordA, recordB) => sortingNumberComparer(
                                    orderQuantityByProducts(recordA.products),
                                    orderQuantityByProducts(recordB.products)
                                ),
                                width: 115
                            },
                            {
                                title: 'Amount',
                                align: 'right',
                                dataIndex: 'products',
                                render: (value: any) => orderTotalByProducts(value).amountFormat,
                                sorter: (recordA, recordB) => sortingNumberComparer(
                                    orderTotalByProducts(recordA.products).amount,
                                    orderTotalByProducts(recordB.products).amount
                                ),
                                width: 200
                            }
                        ]}
                        dataSource={retrieveOrderSap}
                        expandable={{
                            expandedRowRender: _handleProductsByProductSizes
                        }}
                        rowKey={(itemData) => `antd-sap-${itemData.id}`}
                        pagination={false}
                    />
                </BVEmpty>
            </BVShimmer>
        </div>
    );
}

export default CheckoutTableSap;